import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Theme,
  Tooltip,
} from '@mui/material';
import { green } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import { GridRenderCellParams, GridRowData } from '@mui/x-data-grid';
import clsx from 'clsx';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import {
  DataTable,
  IColDef,
} from 'src/app/modules/Manage/pages/queues-table/DataTable';
import { v4 as uuidv4 } from 'uuid';

import { useBusinessPermissions } from '../../common/helperFunctions/useBusinessPermissions';
import {
  loadableEnum,
  useMakePHPRequest,
} from '../../common/useMakePHPRequest';
import {
  trackAddMyVMDrop,
  trackAddVMDrop,
  trackEditVMDrop,
} from '../../common/userTracking';
import { user } from '../../recoil/atoms/user';
import { getAgentVmDrop } from '../../recoil/selectors/getAgentVmDrop';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../services/rest/getApiMutation';
import { endpointEnum } from '../../services/rest/makeAPIRequest';
import { AddVmDropModal } from '../components/modals/AddVmDropModal';
import { DeleteVmModal } from '../components/modals/DeleteVmModal';
import { EditVmDropModal } from '../components/modals/EditVmDropModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { QueuesSkeleton } from '../modules/Skeleton/QueuesSkeleton';
import { DashboardTourExtraSpotlights } from '../pages/dashboardTour/DashboardTourExtraSpotlights';

const useStyles = makeStyles((theme: Theme & { custom: any }) => ({
  messageColumn: {
    width: '100% !important',
    minWidth: '400px !important',
    lineHeight: 'normal !important',
    whiteSpace: 'normal !important' as 'normal',
    padding: theme.spacing(1) + 'px !important',
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -22,
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  spinner: {
    position: 'fixed !important' as 'fixed',
  },
  audio: {
    transition: 'all 0.5s linear',
    boxShadow: '0px 0px 0px 0px',
    backgroundColor: '#f0f3f4',
    borderRadius: '7px 7px 7px 7px',
    height: 32,
    margin: '10px 0px',
    '&:focus': {
      outline: 0,
    },
  },
  actionButton: {
    whiteSpace: 'nowrap',
    flexDirection: 'column',
    display: 'flex',
    alignItems: 'center',
    '& article': {
      margin: 0,
    },
    '& p': {
      lineHeight: 'normal',
      whiteSpace: 'normal',
      textTransform: 'uppercase',
    },
  },
}));
export function VmDrop() {
  const classes = useStyles();
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  // const setDeleteVm = useSetRecoilState(vmDidToDelete);
  const setRefetchToggleForVmDrop = useSetRecoilState(
    refetchToggle(endpointEnum.GET_VM_DROP)
  );
  const [mp3File, setMp3File] = useState('');
  const mp3Ref = useRef();
  // state for add new vm Modal
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  // state for delete vm modal
  const [showA, setShowA] = useState(false);
  const handleCloseA = () => setShowA(false);
  const handleShowA = () => setShowA(true);
  // state for edit vm modal
  const [showB, setShowB] = useState(false);
  const handleCloseB = () => setShowB(false);
  const handleShowB = () => setShowB(true);
  // state for vmId and name being passed to delete modal
  const [vmId, setVmId] = useState('');
  const [vmName, setVmName] = useState('');
  // data for table
  const vmdropData = useRecoilValueLoadable(getAgentVmDrop);
  const vmDropDataCached = useRef([]);
  vmDropDataCached.current = useMemo(
    () =>
      vmdropData.contents?.filteredByAgent
        ? vmdropData.contents?.filteredByAgent?.map((o) => ({
            ...o,
            id: uuidv4(),
          }))
        : vmDropDataCached.current,
    [vmdropData]
  );
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  const loginUser = userAtom.contents?.user ?? {};

  const [deleteStatus, setDeleteStatus] = useState(loadableEnum.EMPTY);
  const [selected, setSelected] = useState<GridRowData[]>([]);
  const [deleteGroupId, setDeleteGroupId] = useState<string | undefined>('');

  const makeRequest = useMakePHPRequest({
    userAtom: userAtom.contents,
    setStatus: setDeleteStatus,
  });
  function changeAction(row) {
    setVmName(row.voicemaildropName);
    setVmId(row.voicemaildropid);
    setMp3File(row.voicemaildropMessage);
    mp3Ref.current = row.voicemaildropMessage;
    handleShowB();
  }
  const mutation = useMutation(getApiMutation(userAtom.contents, setUserAtom));
  function actionsFormatter(params: GridRenderCellParams) {
    return deleteGroupId === params.row.voicemaildropid ? (
      <div className={classes.actionButton}>
        <article>
          <Tooltip title="Confirm Deletion">
            <>
              <IconButton
                aria-label="accept"
                onClick={async (event) => {
                  event.preventDefault(); // stop link
                  // insert action to delete here
                  // await makeRequest({
                  //   methodName: methodNameEnum.DELETE_VM_DROP,
                  //   payload: {
                  // vmdid: params.row.voicemaildropid,
                  // userid: userAtom.contents.userid,
                  //   },
                  // });
                  const data = {
                    call: methodNameEnum.DELETE_VM_DROP,
                    apikey: userAtom.contents?.apikey,
                    businessid: userAtom.contents?.businessid,
                    vmdid: params.row.voicemaildropid,
                    userid: userAtom.contents.userid,
                  };
                  const response = await mutation.mutateAsync({
                    endpoint: endpointEnum.DELETE_VM_DROP,
                    payload: data,
                  });
                  if (response.data.success) {
                    enqueueSnackbarWithStyles({
                      message: 'Successfully Deleted VM Drop',
                      alertProps: { severity: 'success' },
                    });
                  } else {
                    enqueueSnackbarWithStyles({
                      message: 'Encountered an error deleting the VM Drop',
                      alertProps: { severity: 'error' },
                    });
                  }
                  setRefetchToggleForVmDrop({});
                }}
                disabled={
                  mutation.isLoading || deleteStatus === loadableEnum.LOADING
                }
                size="large"
              >
                <CheckIcon
                  color={
                    mutation.isLoading || deleteStatus === loadableEnum.LOADING
                      ? 'disabled'
                      : 'primary'
                  }
                />
                {mutation.isLoading ||
                  (deleteStatus === loadableEnum.LOADING && (
                    <CircularProgress
                      size={43}
                      className={classes.buttonProgress}
                    />
                  ))}
              </IconButton>
            </>
          </Tooltip>
          <Tooltip title="Cancel">
            <>
              <IconButton
                aria-label="cancel"
                onClick={(event) => {
                  event.preventDefault(); // stop link
                  // reset groupid hide confirm / cancel deletion
                  setDeleteGroupId(undefined);
                }}
                disabled={
                  mutation.isLoading || deleteStatus === loadableEnum.LOADING
                }
                size="large"
              >
                <ClearIcon
                  color="disabled"
                  className={clsx({
                    [classes.deleteIconButton]:
                      !mutation.isLoading &&
                      deleteStatus !== loadableEnum.LOADING, // || deleteCancelTokenSource,
                  })}
                />
              </IconButton>
            </>
          </Tooltip>
        </article>
      </div>
    ) : (
      <div>
        <Tooltip title="Edit">
          <IconButton
            name="edit"
            onClick={() => {
              changeAction(params.row);
              trackEditVMDrop();
            }}
            size="large"
          >
            <CreateIcon color="primary" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete">
          <IconButton
            aria-label="delete"
            onClick={() => setDeleteGroupId(params.row.voicemaildropid)}
            size="large"
          >
            <DeleteIcon className={classes.deleteIconButton} />
          </IconButton>
        </Tooltip>
      </div>
    );
  }
  function audioFormatter(params: GridRenderCellParams) {
    return (
      <>
        <audio
          controls
          className={classes.audio}
          src={params.row.voicemaildropMessage}
        ></audio>
      </>
    );
  }
  const readOnly = useBusinessPermissions().accountReadOnly;
  const columns = [
    {
      field: 'voicemaildropName',
      headerName: 'Name',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'voicemaildropMessage',
      headerName: 'Message',
      renderCell: audioFormatter,
      headerClassName: classes.messageColumn,
      cellClassName: classes.messageColumn,
    },
    {
      field: 'voicemaildropid',
      headerName: 'Actions',
      renderCell: actionsFormatter,
      hide: readOnly,
      enableSorting: false,
      headerAlign: 'center',
      align: 'center',
      width: 120,
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
  ] /* satisfies */ as IColDef[];
  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        {...props}
        dataType="VM Drops"
        numSelected={selected.length}
        rightComponent={
          <ToolbarAddDeleteButton
            numSelected={selected.length}
            onClickForAdd={() => {
              handleShow();
              if (
                window.location.pathname ===
                `/my-profile/${loginUser.userid}/vm-drop`
              ) {
                trackAddMyVMDrop();
              } else {
                trackAddVMDrop();
              }
            }}
            onClickForDelete={handleShowA}
            isLoading={props.isLoading}
          />
        }
        centerComponent={
          <Button
            variant="outlined"
            color="primary"
            endIcon={<HelpOutlineIcon />}
            target="_blank"
            href="https://support.kixie.com/hc/en-us/articles/4576581944731-How-does-Voicemail-Drop-Work-"
            rel="noopener noreferrer"
          >
            How to Use Voicemail Drop
          </Button>
        }
      />
    ),
    [handleShow, handleShowA, selected.length]
  );
  return (
    <>
      <DashboardTourExtraSpotlights />
      <Paper square={true} elevation={0}>
        {vmdropData.state === 'loading' && !vmDropDataCached.current.length ? (
          <QueuesSkeleton />
        ) : (
          <DataTable
            enableSelectAll={true}
            setSelected={setSelected}
            loading={vmdropData.state === 'loading'}
            rows={vmDropDataCached?.current ?? []}
            columns={columns}
            sortModel={[
              {
                field: 'voicemaildropName',
                sort: 'asc',
              },
            ]}
            ToolbarComponent={ToolbarComponent}
            selected={selected}
          />
        )}
        <AddVmDropModal
          setRefetchToggleForVmDrop={setRefetchToggleForVmDrop}
          onHide={handleClose}
          // handleShow={handleShow}
          show={show}
        />
        <DeleteVmModal
          setRefetchToggleForVmDrop={setRefetchToggleForVmDrop}
          enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
          selected={selected}
          userAtom={userAtom}
          setSelected={setSelected}
          makeRequest={makeRequest}
          vmName={vmName}
          vmId={vmId}
          handleClose={handleCloseA}
          handleShow={handleShowA}
          show={showA}
          mutation={mutation}
        />
        <EditVmDropModal
          setRefetchToggleForVmDrop={setRefetchToggleForVmDrop}
          vmName={vmName}
          vmId={vmId}
          mp3File={mp3File}
          mp3Ref={mp3Ref}
          onHide={handleCloseB}
          // handleShow={handleShowB}
          show={showB}
        />
      </Paper>
    </>
  );
}
